/**
 * Purpose: Generic confirmation dialog for destructive/irreversible actions.
 *          Replaces window.confirm() at call sites that need styling, async
 *          work, or dark-mode support. Vertical-agnostic — no business imports.
 * Inputs: open, title, message, confirmLabel, cancelLabel, variant
 *         ('danger'|'primary'), loading, onConfirm, onCancel.
 * Outputs: modal dialog; Escape and backdrop click cancel. Returns null when closed.
 */
import React, { useEffect } from 'react';
import CTAButton from './CTAButton';
import useFocusTrap from '../../hooks/useFocusTrap';

const ConfirmDialog = ({
  open,
  title,
  message = null,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const trapRef = useFocusTrap(open);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !loading) onCancel?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, loading, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop: hidden from the a11y tree (tabIndex -1) so it does not duplicate
          the Cancel control's accessible name. Keyboard users cancel via Escape. */}
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        className="absolute inset-0 cursor-default bg-black/50"
        onClick={() => !loading && onCancel?.()}
      />
      <div
        ref={trapRef}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl focus:outline-none dark:bg-gray-900"
      >
        <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">{title}</h2>
        {message && <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{message}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <CTAButton variant="secondary" size="sm" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </CTAButton>
          <CTAButton variant={variant} size="sm" loading={loading} autoFocus onClick={onConfirm}>
            {confirmLabel}
          </CTAButton>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
